// A minimal, dependency-free .xlsx writer — enough to hand an operator a real
// workbook with one sheet per dashboard tab.
//
// ── Why this exists rather than a library ────────────────────────────────────
// The alternative is SheetJS, which is about a megabyte for the one direction we
// need (write, never read) and lands in the client bundle of a page that is
// otherwise plain tables. An .xlsx is a ZIP of a few small XML files, and the
// subset that produces "text and numbers in named sheets" is genuinely short —
// so it is written out here, in the same spirit as the CSV writer in
// AdminPage.js and the QR encoder in utils/qrcode.js.
//
// ── Why not just several CSVs ────────────────────────────────────────────────
// A CSV cannot hold more than one sheet, and the whole request is one file with
// the tabs kept apart. CSV stays available per tab, because it is what a
// mail-merge and every scripting tool actually want.
//
// ── The shape ───────────────────────────────────────────────────────────────
// Entries are STORED, not deflated: no compression means no zlib, and these
// files are tens of kilobytes of text that the operator opens once. Strings are
// written INLINE (`t="inlineStr"`) rather than through a sharedStrings table —
// slightly larger, and it removes the one structure in the format that has to
// stay in step across every sheet.
//
// ⚠️ Note what is deliberately NOT carried over from `csvCell`: the apostrophe
// that defuses a leading `=` / `+` / `-` / `@`. In a CSV those make the cell a
// FORMULA; in xlsx a formula lives in its own `<f>` element and an inline string
// is inert whatever it starts with. Prefixing here would corrupt the value —
// every "-" and every negative number would gain a stray apostrophe.

// ── CRC-32, for the ZIP entries ─────────────────────────────────────────────
let CRC_TABLE = null;
function crcTable() {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Int32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c;
  }
  CRC_TABLE = t;
  return t;
}

function crc32(bytes) {
  const t = crcTable();
  let c = 0 ^ (-1);
  for (let i = 0; i < bytes.length; i++) c = (c >>> 8) ^ t[(c ^ bytes[i]) & 0xFF];
  return (c ^ (-1)) >>> 0;
}

const utf8 = (s) => new TextEncoder().encode(s);

// ── XML ─────────────────────────────────────────────────────────────────────

/**
 * Escape a value for XML text, and drop the characters the format forbids.
 *
 * ⚠️ The control-character strip is not cosmetic: a raw 0x00–0x08 or 0x0B in a
 * sheet makes Excel declare the whole workbook corrupt and offer to repair it,
 * which reads as "the export is broken" rather than "one report had a stray
 * byte in it". Tab, newline and carriage return are legal and kept.
 */
function xmlText(v) {
  return String(v)
    // eslint-disable-next-line no-control-regex
    .replace(/[ --]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0 → "A", 25 → "Z", 26 → "AA". Spreadsheet column names are base-26 with no zero. */
function colName(i) {
  let n = i + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/**
 * A sheet name Excel will actually accept.
 *
 * The rules are its own: 31 characters, and none of `[ ] : * ? / \`. A name that
 * breaks either is not rejected on open — it makes the file unreadable, so the
 * tidying happens here rather than being left to whoever adds the next tab.
 * Uniqueness is enforced by the caller passing a `used` set, because two sheets
 * with one name is the other way to produce an unopenable workbook.
 */
export function safeSheetName(name, used) {
  let base = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || 'Sheet';
  if (!used) return base;
  let out = base;
  let n = 2;
  while (used.has(out.toLowerCase())) {
    const suffix = ` (${n})`;
    out = base.slice(0, 31 - suffix.length) + suffix;
    n++;
  }
  used.add(out.toLowerCase());
  return out;
}

/**
 * One cell.
 *
 * A finite number is written as a number so the operator can sum a column;
 * everything else is an inline string. `null`/`undefined`/`''` produce an EMPTY
 * cell rather than the text "null" — a blank is what a spreadsheet means by
 * "nothing was recorded here", and it is what lets COUNTA be right.
 */
function cellXml(value, ref, styleIdx) {
  if (value == null || value === '') return '';
  const s = styleIdx ? ` s="${styleIdx}"` : '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${s}><v>${value}</v></c>`;
  }
  if (typeof value === 'boolean') {
    return `<c r="${ref}"${s} t="inlineStr"><is><t>${value ? 'yes' : 'no'}</t></is></c>`;
  }
  const text = xmlText(value);
  // xml:space="preserve" so a value that is meaningfully padded survives.
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
}

function sheetXml(sheet) {
  const header = sheet.header || [];
  const rows = sheet.rows || [];
  const out = [];
  out.push('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>');
  out.push('<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">');
  // A frozen header row: these tables run to thousands of rows and the whole
  // point of the workbook is scrolling them.
  if (header.length) {
    out.push('<sheetViews><sheetView workbookViewId="0">'
      + '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
      + '</sheetView></sheetViews>');
  }
  out.push('<sheetData>');
  if (header.length) {
    const cells = header.map((h, c) => cellXml(h, `${colName(c)}1`, 1)).join('');
    out.push(`<row r="1">${cells}</row>`);
  }
  rows.forEach((row, r) => {
    const rowNum = r + (header.length ? 2 : 1);
    const cells = (row || []).map((v, c) => cellXml(v, `${colName(c)}${rowNum}`, 0)).join('');
    out.push(`<row r="${rowNum}">${cells}</row>`);
  });
  out.push('</sheetData></worksheet>');
  return out.join('');
}

// Two fonts, so the header row is bold. Style index 1 is the bold one; index 0
// is the default every data cell uses.
const STYLES_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
  + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
  + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>'
  + '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
  + '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>'
  + '<borders count="1"><border/></borders>'
  + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
  + '<cellXfs count="2">'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
  + '</cellXfs>'
  // The named "Normal" style. Excel tolerates its absence but a reader is
  // entitled to expect it — openpyxl warns that the workbook has no default
  // style and substitutes its own, which is a warning on a file we produced.
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

// ── ZIP (stored entries only) ───────────────────────────────────────────────

function dosTime(d) {
  const time = ((d.getHours() & 31) << 11) | ((d.getMinutes() & 63) << 5) | ((d.getSeconds() / 2) & 31);
  const date = (((d.getFullYear() - 1980) & 127) << 9) | (((d.getMonth() + 1) & 15) << 5) | (d.getDate() & 31);
  return { time, date };
}

function zip(files, now = new Date()) {
  const { time, date } = dosTime(now);
  const chunks = [];
  const central = [];
  let offset = 0;

  for (const f of files) {
    const nameBytes = utf8(f.name);
    const body = utf8(f.data);
    const crc = crc32(body);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);          // version needed
    local.setUint16(6, 0x0800, true);      // flags: UTF-8 names
    local.setUint16(8, 0, true);           // method 0 = stored
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, body.length, true);
    local.setUint32(22, body.length, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);
    chunks.push(new Uint8Array(local.buffer), nameBytes, body);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);             // version made by
    cd.setUint16(6, 20, true);             // version needed
    cd.setUint16(8, 0x0800, true);
    cd.setUint16(10, 0, true);
    cd.setUint16(12, time, true);
    cd.setUint16(14, date, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, body.length, true);
    cd.setUint32(24, body.length, true);
    cd.setUint16(28, nameBytes.length, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), nameBytes);

    offset += 30 + nameBytes.length + body.length;
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  const all = [...chunks, ...central, new Uint8Array(end.buffer)];
  const total = all.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  for (const c of all) { out.set(c, p); p += c.length; }
  return out;
}

// ── The one entry point ─────────────────────────────────────────────────────

/**
 * Build a workbook. `sheets` is `[{ name, header, rows }]`.
 *
 * Returns the raw bytes, so this stays testable outside a browser — the Blob
 * and the click live in `downloadXlsx` below.
 */
export function buildXlsx(sheets) {
  const used = new Set();
  const list = (sheets || [])
    .filter(Boolean)
    .map((s) => ({ ...s, name: safeSheetName(s.name, used) }));
  if (!list.length) throw new Error('A workbook needs at least one sheet.');

  const files = [
    {
      name: '[Content_Types].xml',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
        + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
        + list.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" `
          + 'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>').join('')
        + '</Types>',
    },
    {
      name: '_rels/.rels',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
        + '</Relationships>',
    },
    {
      name: 'xl/workbook.xml',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
        + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>'
        + list.map((s, i) => `<sheet name="${xmlText(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
        + '</sheets></workbook>',
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + list.map((_, i) => `<Relationship Id="rId${i + 1}" `
          + 'Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" '
          + `Target="worksheets/sheet${i + 1}.xml"/>`).join('')
        // The styles part is numbered after the sheets so the ids never collide.
        + `<Relationship Id="rId${list.length + 1}" `
        + 'Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" '
        + 'Target="styles.xml"/>'
        + '</Relationships>',
    },
    { name: 'xl/styles.xml', data: STYLES_XML },
    ...list.map((s, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: sheetXml(s) })),
  ];

  return zip(files);
}

/** Build the workbook and hand it to the browser as a download. */
export function downloadXlsx(filename, sheets) {
  const bytes = buildXlsx(sheets);
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoked on a later tick, for the reason downloadCsv documents: Safari has
  // not always finished reading the blob when click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
